import Box from '@mui/material/Box';
import Shimmer from './Shimmer';

// Skeleton for the sample-tracking page.
//
// Rebuilt for v5. The old version drew a forest-green hero and a
// Container-based two-column grid from the previous design; the page is
// now a full-width header over a 3:1 split, so the skeleton flashed the
// wrong shape and then snapped into place. Track is usually opened from
// a WhatsApp deep link, so this skeleton is the first thing a patient
// sees — it is worth it being the right shape.
//
// Mirrors the real layout: header, then a left column holding the map
// panel and the "Sample journey" timeline, beside the Trustian card and
// two detail cards.

const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const StageRowShimmer = ({ last = false }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 1.75 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Shimmer width={32} height={32} circular />
      {!last && <Box sx={{ flex: 1, width: 3, minHeight: 18, bgcolor: '#E1EBE8' }} />}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.5, pb: 2.25 }}>
      <Shimmer width="46%" height={16} rounded={6} />
      <Shimmer width="68%" height={13} rounded={6} />
    </Box>
  </Box>
);

const TrackShimmer = () => (
  <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
    {/* ── header ──────────────────────────────────────────────────── */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', justifyContent: 'space-between', gap: 2, p: '12px 8px 4px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Shimmer width={210} height={13} rounded={6} />
        <Shimmer width={330} height={44} rounded={10} />
      </Box>
      <Shimmer width={300} height={52} rounded={999} />
    </Box>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mt: 1.5 }}>
      {/* ── left ──────────────────────────────────────────────────── */}
      <Box sx={{ flex: '3 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* the map panel keeps its real mint plate so the swap is quiet */}
        <Box
          sx={{
            minHeight: 380,
            borderRadius: '28px',
            bgcolor: '#D5EFE8',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Shimmer width={190} height={44} rounded={999} sx={{ opacity: 0.55 }} />
        </Box>

        <Box component="section" sx={card}>
          <Shimmer width={220} height={30} rounded={8} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {[0, 1, 2, 3, 4].map((i) => <StageRowShimmer key={i} last={i === 4} />)}
          </Box>
        </Box>
      </Box>

      {/* ── aside ─────────────────────────────────────────────────── */}
      <Box component="aside" sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Trustian */}
        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Shimmer width={64} height={64} circular />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1, minWidth: 0 }}>
              <Shimmer width="62%" height={18} rounded={6} />
              <Shimmer width="44%" height={13} rounded={6} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Shimmer width={86} height={26} rounded={999} />
            <Shimmer width={120} height={26} rounded={999} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Shimmer height={44} rounded={16} />
            <Shimmer height={44} rounded={16} />
          </Box>
        </Box>

        {/* two detail cards */}
        {[0, 1].map((i) => (
          <Box key={i} sx={{ ...card, gap: 1.25 }}>
            <Shimmer width="54%" height={16} rounded={6} />
            <Shimmer width="100%" height={13} rounded={6} />
            <Shimmer width="82%" height={13} rounded={6} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export default TrackShimmer;
